'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import {
  ArrowRight,
  BadgeCheck,
  CalendarCheck,
  Car,
  Clock,
  Cog,
  Heart,
  Luggage,
  MapPin,
  Search,
  Users,
} from 'lucide-react';

import { NewsletterSection } from '@/components/site/NewsletterSection';
import { TestimonialsSection } from '@/components/site/TestimonialsSection';
import { CarIllustration } from '@/components/site/CarIllustrations';
import { useCurrency } from '@/components/site/CurrencyContext';
import { usePublicBrand } from '@/components/site/PublicBrandProvider';
import { RENTAL_LOCATIONS } from '@/lib/data';
import { useLocale } from '@/lib/i18n';
import { sectionEnabled } from '@/lib/storefront/designs';
import type { Vehicle } from '@/types';
import { featuredVehicles, type HomeDesignProps } from './home-props';

const CREAM = 'bg-[#FAF6F0]';
const HERO_CAR =
  'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=1400&q=80';
const WHY_CAR =
  'https://images.unsplash.com/photo-1541899481282-d53bffe3c35d?auto=format&fit=crop&w=1200&q=80';

const WHY_ITEMS = [
  { icon: BadgeCheck, title: 'ed.why.1t', description: 'ed.why.1d' },
  { icon: Car, title: 'ed.why.2t', description: 'ed.why.2d' },
  { icon: Clock, title: 'ed.why.3t', description: 'ed.why.3d' },
  { icon: CalendarCheck, title: 'ed.why.4t', description: 'ed.why.4d' },
];

/**
 * Bright editorial design: cream canvas, two-tone headline, a search card cut
 * into the hero, an inventory grid and a why-choose-us block.
 */
export function EditorialHome(props: HomeDesignProps) {
  const { vehicles, storefront, appearance } = props;

  return (
    <>
      <EditorialHero {...props} />

      {sectionEnabled(appearance, 'inventory') && (
        <InventoryGrid {...props} vehicles={featuredVehicles(vehicles, storefront)} />
      )}
      {sectionEnabled(appearance, 'whyChooseUs') && <WhyChooseUs />}
      {sectionEnabled(appearance, 'numbers') && <NumbersStrip vehicles={vehicles} />}
      {sectionEnabled(appearance, 'testimonials') && (
        <TestimonialsSection testimonials={storefront?.testimonials ?? []} />
      )}
      {sectionEnabled(appearance, 'newsletter') && <NewsletterSection />}
    </>
  );
}

function EditorialHero({
  storefront,
  onBookAny,
  onSearch,
}: HomeDesignProps) {
  const { t } = useLocale();
  const brand = usePublicBrand();
  const [imageFailed, setImageFailed] = useState(false);

  const headline = storefront?.hero.headline ?? '';
  const description = storefront?.hero.description ?? '';
  const heroImage = storefront?.hero.imageUrl ?? '';
  // The agency's own hero image wins; the packaged photo is only a fallback.
  const usableHeroImage = heroImage.startsWith('/') || /^https?:\/\//.test(heroImage);
  const heroSource = usableHeroImage ? heroImage : HERO_CAR;

  return (
    <section className={`relative ${CREAM}`}>
      <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 pb-44 pt-14 sm:px-8 lg:grid-cols-2 lg:pt-20">
        <div className="min-w-0">
          <span className="text-xs font-black uppercase tracking-[0.2em] text-[var(--color-primary)]">
            {t('ed.kicker')}
          </span>
          <h1 className="mt-3 break-words text-[32px] font-extrabold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-[52px]">
            {headline || (
              <>
                {t('ed.titleA')} <span className="text-[var(--color-primary)]">{t('ed.titleB')}</span>
              </>
            )}
          </h1>
          <p className="mt-5 max-w-lg text-sm leading-relaxed text-slate-600 sm:text-base">
            {description || t('ed.sub')}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={onBookAny}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--color-primary)] px-6 py-3 text-xs font-bold uppercase tracking-wider text-white shadow-lg shadow-orange-950/10 transition-all hover:bg-[var(--color-primary-dark)] active:scale-95"
            >
              <span>{storefront?.hero.ctaLabel || t('ed.cta')}</span>
              <ArrowRight className="h-4 w-4" />
            </button>
            {brand.phone ? (
              <a
                href={`tel:${brand.phone.replace(/\s/g, '')}`}
                className="rounded-full border border-slate-300 px-6 py-3 text-xs font-bold uppercase tracking-wider text-slate-800 transition-colors hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
              >
                {brand.phone}
              </a>
            ) : null}
          </div>
        </div>

        <div className="relative">
          <div className="relative h-[240px] w-full overflow-hidden rounded-[2.5rem] shadow-2xl sm:h-[380px]">
            {imageFailed ? (
              <CarIllustration type="bmw_5series" className="h-full w-full object-contain p-6" />
            ) : (
              <Image
                src={heroSource}
                alt=""
                fill
                priority
                className="object-cover"
                referrerPolicy="no-referrer"
                onError={() => setImageFailed(true)}
              />
            )}
          </div>
          <div className="absolute -bottom-6 -left-4 hidden rounded-2xl bg-white px-5 py-4 shadow-xl sm:block">
            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-900">
              <BadgeCheck className="h-4 w-4 text-[var(--color-primary)]" />
              {t('nav.freeCancel')}
            </span>
            <span className="mt-1 block text-[11px] text-slate-500">{t('nav.support')}</span>
          </div>
        </div>
      </div>

      <div className="relative z-20 mx-auto -mt-32 max-w-5xl px-4 sm:px-8">
        <EditorialSearchCard onSearch={onSearch} />
      </div>
    </section>
  );
}

/** White location card with a single search action. */
function EditorialSearchCard({ onSearch }: { onSearch: HomeDesignProps['onSearch'] }) {
  const { t } = useLocale();
  const [pickup, setPickup] = useState(RENTAL_LOCATIONS[0] ? `${RENTAL_LOCATIONS[0].name} (${RENTAL_LOCATIONS[0].code})` : 'Agadir');
  const [dropoff, setDropoff] = useState(pickup);

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSearch({
          location: pickup,
          pickupDate: '2026-10-18',
          pickupTime: '10:00 AM',
          dropoffDate: '2026-10-22',
          dropoffTime: '10:00 AM',
          rentalType: 'daily',
        });
      }}
      className="grid gap-3 rounded-3xl border border-slate-200/80 bg-white p-4 shadow-2xl shadow-slate-900/5 sm:grid-cols-[1fr_1fr_auto] sm:items-end sm:p-5"
    >
      <label className="block rounded-2xl border border-slate-200/90 bg-slate-50 px-4 py-3">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">
          {t('search.pickupLoc')}
        </span>
        <span className="flex items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
          <select
            value={pickup}
            onChange={(event) => setPickup(event.target.value)}
            className="w-full cursor-pointer bg-transparent text-xs font-semibold text-slate-900 outline-none sm:text-sm"
          >
            {RENTAL_LOCATIONS.map((location) => (
              <option key={location.id} value={`${location.name} (${location.code})`}>
                {location.name} ({location.code})
              </option>
            ))}
          </select>
        </span>
      </label>

      <label className="block rounded-2xl border border-slate-200/90 bg-slate-50 px-4 py-3">
        <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-500">
          {t('search.dropDate')}
        </span>
        <span className="flex items-center gap-2">
          <MapPin className="h-4 w-4 shrink-0 text-[var(--color-primary)]" />
          <select
            value={dropoff}
            onChange={(event) => setDropoff(event.target.value)}
            className="w-full cursor-pointer bg-transparent text-xs font-semibold text-slate-900 outline-none sm:text-sm"
          >
            {RENTAL_LOCATIONS.map((location) => (
              <option key={location.id} value={`${location.name} (${location.code})`}>
                {location.name} ({location.code})
              </option>
            ))}
          </select>
        </span>
      </label>

      <button
        type="submit"
        className="inline-flex h-[58px] items-center justify-center gap-2 rounded-2xl bg-[var(--color-primary)] px-6 text-xs font-bold uppercase tracking-wider text-white shadow-md transition-all hover:bg-[var(--color-primary-dark)] active:scale-[0.98]"
      >
        <Search className="h-4 w-4" />
        <span>{t('search.cta')}</span>
      </button>
    </form>
  );
}

/** Bordered inventory grid with spec badges and a view-all link. */
function InventoryGrid({
  vehicles,
  favorites,
  onToggleFavorite,
  onBookVehicle,
  onViewAll,
  onViewDetails,
}: HomeDesignProps) {
  const { t } = useLocale();
  const { fmt, equiv } = useCurrency();
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  return (
    <section className={`${CREAM} py-14 lg:py-20`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <span className="mb-1 block text-xs font-black uppercase tracking-wider text-[var(--color-primary)]">
              {t('ed.inventory.kicker')}
            </span>
            <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
              {t('ed.inventory.title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onViewAll}
            className="group inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[var(--color-primary)] transition-colors hover:text-[var(--color-primary-dark)]"
          >
            <span>{t('ed.inventory.viewAll')}</span>
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {vehicles.slice(0, 6).map((car) => {
            const isFavorite = favorites.includes(car.id);
            const hasError = imageErrors[car.id] || !car.imageUrl;
            return (
              <article
                key={car.id}
                onClick={() => onViewDetails(car)}
                className="group flex cursor-pointer flex-col rounded-3xl border border-[#e9e0d2] bg-white p-4 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
                    {car.category}
                  </span>
                  <button
                    type="button"
                    aria-label="Add to favorites"
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleFavorite(car.id);
                    }}
                    className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-500"
                  >
                    <Heart className={`h-4 w-4 ${isFavorite ? 'fill-rose-500 text-rose-500' : ''}`} />
                  </button>
                </div>

                <div className="relative mt-2 h-40 w-full overflow-hidden rounded-2xl bg-slate-50">
                  {hasError ? (
                    <CarIllustration
                      type={car.imageType}
                      className="h-full w-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <Image
                      src={car.imageUrl ?? ''}
                      alt={car.name}
                      fill
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                      referrerPolicy="no-referrer"
                      onError={() => setImageErrors((current) => ({ ...current, [car.id]: true }))}
                    />
                  )}
                </div>

                <h3 className="mt-4 text-base font-extrabold tracking-tight text-slate-900">{car.name}</h3>

                <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] font-medium text-slate-500">
                  <span className="flex items-center gap-1">
                    <Users className="h-3.5 w-3.5 text-slate-400" />
                    {car.seats} {t('pop.seats')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Cog className="h-3.5 w-3.5 text-slate-400" />
                    {car.transmission}
                  </span>
                  <span className="flex items-center gap-1">
                    <Luggage className="h-3.5 w-3.5 text-slate-400" />
                    {car.bags} {t('pop.bags')}
                  </span>
                </div>

                <div className="mt-5 flex items-center justify-between border-t border-[#f0e8db] pt-4">
                  <div>
                    <span className="text-xl font-black tracking-tight text-[var(--color-primary)]">
                      {fmt(car.pricePerDay)}
                    </span>
                    <span className="text-xs font-medium text-slate-400"> / day</span>
                    {equiv(car.pricePerDay) ? (
                      <div className="text-[11px] text-slate-400">{equiv(car.pricePerDay)}</div>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onBookVehicle(car);
                    }}
                    className="whitespace-nowrap rounded-full bg-slate-900 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-white transition-colors hover:bg-[var(--color-primary)]"
                  >
                    {t('pop.bookNow')}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/** Photo beside the agency's selling points. */
function WhyChooseUs() {
  const { t } = useLocale();
  return (
    <section className="bg-white py-14 lg:py-20">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-8 lg:grid-cols-2">
        <div className="relative h-[260px] overflow-hidden rounded-[2.5rem] shadow-xl sm:h-[420px]">
          <Image
            src={WHY_CAR}
            alt=""
            fill
            className="object-cover"
            referrerPolicy="no-referrer"
          />
        </div>
        <div>
          <span className="mb-1 block text-xs font-black uppercase tracking-wider text-[var(--color-primary)]">
            {t('ed.why.kicker')}
          </span>
          <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">
            {t('ed.why.title')}
          </h2>
          <div className="mt-8 space-y-6">
            {WHY_ITEMS.map((item) => (
              <div key={item.title} className="flex gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#FAF6F0] text-[var(--color-primary)]">
                  <item.icon className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-slate-900">{t(item.title)}</span>
                  <span className="mt-1 block text-xs leading-relaxed text-slate-500">
                    {t(item.description)}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/** Fleet figures read from the catalogue the agency actually publishes. */
function NumbersStrip({ vehicles }: { vehicles: Vehicle[] }) {
  const { t } = useLocale();
  const categories = new Set(vehicles.map((vehicle) => vehicle.category));
  const stats = [
    { value: vehicles.length, label: 'ed.num.vehicles' },
    { value: categories.size, label: 'ed.num.categories' },
    { value: RENTAL_LOCATIONS.length, label: 'ed.num.locations' },
  ];

  return (
    <section className={`${CREAM} py-14 lg:py-20`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-8">
        <div className="mb-8 text-center">
          <span className="mb-1 block text-xs font-black uppercase tracking-wider text-[var(--color-primary)]">
            {t('ed.num.kicker')}
          </span>
        </div>
        <div className="grid gap-6 sm:grid-cols-3">
          {stats.map((stat) => (
            <div key={stat.label} className="rounded-3xl border border-[#e9e0d2] bg-white p-8 text-center">
              <span className="block text-4xl font-black tracking-tight text-[var(--color-primary)]">
                {stat.value}
              </span>
              <span className="mt-2 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t(stat.label)}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
